import ActionIconButton from '@components/Common/ActionIconButton';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import { MetricColumn } from '@components/Common/MetricTable';
import { Panel } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { Button, Chip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { isProblem, monthLabel, ProviderProblem, usd } from './costData';
import { CostSection, CostSetupState, CostTiles } from './CostParts';

/*
 * Google Cloud, from the Cloud Billing export in BigQuery: this month and last, by service, with
 * the largest SKUs. The billing account may be in rupees, so amounts are shown in its currency.
 */

interface GcpService {
  service: string;
  cost: number;
  skus: Array<{ sku: string; cost: number }>;
}
interface GcpMonth {
  month: string;
  total: number;
  services: GcpService[];
}
interface GcpUsage {
  state: 'ok';
  currency: string;
  current: GcpMonth;
  last: GcpMonth;
  monthly_limit_cents: number | null;
}

/** Minor units in the billing account's currency. */
export function money(minor: number, currency: string): string {
  if (currency === 'USD') return usd(minor);
  if (!Number.isFinite(minor)) return '-';
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${(minor / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface Cap {
  method: string;
  cap: string;
}

const PLACES_CAPS: Cap[] = [
  { method: 'Place Details', cap: '10 per day' },
  { method: 'Text Search', cap: '5 per day' },
  { method: 'All other Places methods', cap: '0 (turned off)' },
];

const PlacesCaps: React.FC = () => (
  <CostSection<Cap>
    title="Places API daily caps"
    icon={<PlaceOutlinedIcon />}
    meta="Quota limits"
    columns={[
      { key: 'method', header: 'Method', variant: 'name', render: (r) => r.method },
      { key: 'cap', header: 'Daily cap', align: 'left', render: (r) => r.cap },
    ]}
    rows={PLACES_CAPS}
    getRowKey={(r) => r.method}
    footnote="Set as quotas in the Google Cloud console, so Places spend cannot run away: a request past its cap is refused, not billed."
  />
);

const GooglePanel: React.FC = () => {
  const [data, setData] = useState<GcpUsage | ProviderProblem | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setData((await axios.get('/api/gcp-costs')).data);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  let body: React.ReactNode;
  if (loading && !data) body = <SkeletonLoader rows={6} columns={3} />;
  else if (isProblem(data)) body = <CostSetupState provider="Google Cloud" problem={data} onRetry={load} />;
  else if (failed || !data) {
    body = (
      <Panel>
        <EmptyState
          title="Google Cloud costs could not be loaded"
          body="The billing export did not answer. Try again in a moment."
          actions={<Button variant="outlined" onClick={load}>Try again</Button>}
        />
      </Panel>
    );
  } else {
    const cur = data.currency;
    const columns: MetricColumn<GcpService>[] = [
      { key: 'service', header: 'Service', variant: 'name', render: (r) => r.service },
      { key: 'cost', header: `Cost (${cur})`, align: 'right', render: (r) => <span className="ac-amount">{money(r.cost, cur)}</span>, sortValue: (r) => r.cost },
      {
        key: 'skus',
        header: 'Largest SKUs',
        align: 'left',
        render: (r) => (
          <span className="ac-chips">
            {r.skus.slice(0, 3).map((s) => (
              <Chip key={s.sku} size="small" label={`${s.sku} ${money(s.cost, cur)}`} />
            ))}
          </span>
        ),
      },
    ];
    body = (
      <>
        <CostTiles
          tiles={[
            { label: 'This month', value: money(data.current.total, cur), sublabel: monthLabel(data.current.month), icon: <PaymentsOutlinedIcon /> },
            { label: 'Last month', value: money(data.last.total, cur), sublabel: monthLabel(data.last.month), icon: <HistoryOutlinedIcon /> },
            { label: 'Services with spend', value: String(data.current.services.length), sublabel: 'This month', icon: <CategoryOutlinedIcon /> },
          ]}
        />
        <CostSection<GcpService>
          title="This month by service"
          icon={<TableRowsOutlinedIcon />}
          meta={monthLabel(data.current.month)}
          columns={columns}
          rows={data.current.services}
          getRowKey={(r) => r.service}
          footnote="Net of credits, from the Cloud Billing export. The export lands a few times a day and is read at most every six hours."
        />
        <CostSection<GcpService>
          title="Last month by service"
          icon={<HistoryOutlinedIcon />}
          meta={monthLabel(data.last.month)}
          columns={columns}
          rows={data.last.services}
          getRowKey={(r) => r.service}
        />
      </>
    );
  }

  return (
    <>
      <FilterRow className="ac-toolbar">
        <ActionIconButton label="Refresh" icon={<RefreshIcon />} busy={loading} onClick={load} />
      </FilterRow>
      {body}
      <PlacesCaps />
    </>
  );
};

export default GooglePanel;
